<template>
	<Skeleton :paragraph="{ rows: 9 }" :title="false" :loading="loading">
		<Form :model="formState" :label-col="{ span: 12 }" :wrapper-col="{ span: 12 }" :colon="false" labelWrap>
			<FormItem :label="$t('settings.sub-networks.vrsSettingsForm.title')" v-bind="validateInfos.title">
				<Input v-model:value="formState.title" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="showVrsFields" :label="$t('settings.vrsSettingsForm.radiusVrs')" v-bind="validateInfos.radiusVrs">
				<InputDecimal v-model="formState.radiusVrs" :allowPoint="true" range="positive" :disabled="settings.deleted" />
			</FormItem>
			<FormItem :label="$t('settings.sub-networks.vrsSettingsForm.procd')" v-bind="validateInfos.procd">
				<template #label>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.sub-networks.vrsSettingsForm.procdHint') }}</template>
						<QuestionCircleOutlined /> {{ $t('settings.sub-networks.vrsSettingsForm.procd') }}
					</Tooltip>
				</template>
				<InputDecimal v-model="formState.procd" :allowPoint="false" range="positive" :disabled="settings.deleted" />
			</FormItem>
			<FormItem :label="$t('settings.sub-networks.vrsSettingsForm.measurementsTimeInterval')" v-bind="validateInfos.measurementsTimeInterval">
				<Select v-model:value="formState.measurementsTimeInterval" :options="measurementsTimeIntervalOptions" :disabled="settings.deleted" />
			</FormItem>
			<SettingsInfoBanner v-if="restartRequired" status="warning" class="pd-md-vertical">
				{{ $t('settings.sub-networks.vrsSettingsForm.restartRequiredAfterAdditionalSettingsChange') }}
			</SettingsInfoBanner>
			<FormItem v-if="sessionStore.debug" :label="$t('settings.sub-networks.vrsSettingsForm.dopplerSlipCatcherEnabled')">
				<Switch v-model:checked="formState.dopplerSlipCatcherEnabled" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="sessionStore.debug" :label="$t('settings.sub-networks.vrsSettingsForm.classicVrsEnabled')">
				<Switch v-model:checked="formState.classicVrsEnabled" :disabled="settings.deleted" />
			</FormItem>
			<TypographyParagraph v-if="showVrsFields" class="pd-vertical">{{ $t('settings.sub-networks.vrsSettingsForm.gssMaxDistHint') }}</TypographyParagraph>
			<FormItem v-if="showVrsFields" :label="$t('settings.sub-networks.vrsSettingsForm.gssMaxDist')" v-bind="validateInfos.gssMaxDist">
				<InputDecimal v-model="formState.gssMaxDist" :allowPoint="false" range="positive" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="showVrsFields" :label="$t('settings.sub-networks.vrsSettingsForm.closestGssMinCount')" v-bind="validateInfos.closestGssMinCount">
				<InputDecimal v-model="formState.closestGssMinCount" :allowPoint="false" range="positive" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="showVrsFields" :label="$t('settings.sub-networks.vrsSettingsForm.closestMaxDist')" v-bind="validateInfos.closestMaxDist">
				<InputDecimal v-model="formState.closestMaxDist" :allowPoint="false" range="positive" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="showVrsFields" :label="$t('settings.sub-networks.vrsSettingsForm.gssMaxCount')" v-bind="validateInfos.gssMaxCount">
				<InputDecimal v-model="formState.gssMaxCount" :allowPoint="false" range="positive" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="showVrsFields" :label="$t('settings.sub-networks.vrsSettingsForm.convBaseStationsThreshold')" v-bind="validateInfos.convBaseStationsThreshold">
				<InputDecimal v-model="formState.convBaseStationsThreshold" :allowPoint="false" range="percents" :disabled="settings.deleted" />
			</FormItem>
			<FormItem v-if="showVrsFields" :label="$t('settings.sub-networks.vrsSettingsForm.convSatellitesThreshold')" v-bind="validateInfos.convSatellitesThreshold">
				<InputDecimal v-model="formState.convSatellitesThreshold" :allowPoint="false" range="percents" :disabled="settings.deleted" />
			</FormItem>
			<SettingsInfoBanner v-if="restartRequired" status="warning" class="pd-md-vertical">
				{{ $t('settings.sub-networks.vrsSettingsForm.restartRequiredAfterAdditionalSettingsChange') }}
			</SettingsInfoBanner>
			<FormButtonsItem>
				<Space>
					<Button type="primary" @click.prevent="onSubmit" :loading="isSubmitting" :disabled="settings.deleted">
						{{ $t('settings.vrsSettingsForm.buttonLabel') }}
					</Button>
					<Tooltip v-if="settings.status === 'stopped'" :title="$t('settings.sub-networks.vrsSettingsForm.restartDisabledTooltip')">
						<span>
							<PopconfirmButton
								:title="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.title')"
								:ok-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.confirm')"
								:cancel-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.cancel')"
								:loading="isSubmitting"
								:disabled="settings.deleted || settings.status === 'stopped'"
								:onConfirm="onRestartConfirm"
							>
								{{ $t('settings.vrsSettingsForm.activeSignalsMessages.restartButtonLabel') }}
							</PopconfirmButton>
						</span>
					</Tooltip>
					<PopconfirmButton
						v-else
						:title="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.title')"
						:ok-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.confirm')"
						:cancel-text="$t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.cancel')"
						:loading="isSubmitting"
						:disabled="settings.deleted || settings.status === 'stopped'"
						:onConfirm="onRestartConfirm"
					>
						{{ $t('settings.vrsSettingsForm.activeSignalsMessages.restartButtonLabel') }}
					</PopconfirmButton>
				</Space>
			</FormButtonsItem>
		</Form>
	</Skeleton>
</template>

<script>
import { ref, reactive, nextTick, watch, toRef, computed } from 'vue';
import { Button, Skeleton, Form, FormItem, Input, Tooltip, TypographyParagraph, Space, Select, Switch, message } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';
import Validators from '@/api/Validators';
import { useSessionStore } from '@/stores/session';
import { useLicenseStore } from '@/stores/licenseStore';
import { useI18n } from 'vue-i18n'

import { useVRSSettingsPost } from '@/components/settings/useVRSSettingsPost.js'

import InputDecimal from '@/components/common/InputDecimal.vue';
import FormButtonsItem from '@/components/common/FormButtonsItem.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import SettingsInfoBanner from '@/components/common/SettingsInfoBanner.vue';

import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';

const M_IN_KM = 1000;

export default {
	components: {
		Button, Skeleton, Form, FormItem, Input, Tooltip, TypographyParagraph, QuestionCircleOutlined,
		Space, Select, Switch,
		InputDecimal, FormButtonsItem, PopconfirmButton, SettingsInfoBanner,
	},
	props: ['loading', 'settings', 'fetchSettings', 'onUpdateField'],
	setup(props) {
		const { t } = useI18n();
		const formState = reactive({
			title: '',
			radiusVrs: 0,
			procd: 0,
			measurementsTimeInterval: 1,
			dopplerSlipCatcherEnabled: false,
			classicVrsEnabled: false,
			gssMaxDist: 0,
			closestGssMinCount: 0,
			closestMaxDist: 0,
			gssMaxCount: 0,
			convBaseStationsThreshold: 0,
			convSatellitesThreshold: 0,
		});
		const initialTitle = ref('');
		const initialMeasurementsTimeInterval = ref(1);
		const { validate, validateInfos } = Validators.useSubNetworksOtherFormRules(formState);
		const sessionStore = useSessionStore();
		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(formState, formChangedStatuses, 'OtherSettingsForm');
		const restartRequired = computed(() => formState.measurementsTimeInterval !== initialMeasurementsTimeInterval.value);
		const measurementsTimeIntervalOptions = computed(() => {
			return Consts.SUBNETWORK_MEASUREMENTS_TIME_INTERVALS.map(value => ({
				value,
				label: t('settings.vrsSettingsForm.cssrMessagesFilters.period.seconds', value),
			}));
		});
		
		const settingsRef = toRef(props, 'settings');
		const licenseStore = useLicenseStore();
		const showVrsFields = computed(() => licenseStore.allowVrs && Boolean(settingsRef.value.ivrsEnabled));
		const formStateProcessor = formState => {
			let result = Utils.toObject(formState);
			result.radiusVrs = result.radiusVrs * M_IN_KM;
			result.gssMaxDist = result.gssMaxDist * M_IN_KM;
			result.closestMaxDist = result.closestMaxDist * M_IN_KM;
			result.id = settingsRef.value.id;
			return result;
		}
		const { isSubmitting, onSubmit } = useVRSSettingsPost(
			Api.subNetworkSettingsPost,
			formState,
			validate,
			formStateProcessor,
			resetChangedState,
			null,
			() => {
				if (formState.title != initialTitle.value) {
					props.fetchSettings();
				}
			}
		);
		watch(
			settingsRef,
			value => {
				pauseWatch();
				Utils.updateObject(formState, value);
				formState.radiusVrs = value.radiusVrs / M_IN_KM;
				formState.gssMaxDist = value.gssMaxDist / M_IN_KM;
				formState.closestMaxDist = value.closestMaxDist / M_IN_KM;
				initialTitle.value = value.title;
				initialMeasurementsTimeInterval.value = value.measurementsTimeInterval;
				nextTick(enableWatch);
			},
		);
		const onRestartConfirm = () => {
			props.onUpdateField('status', 'restarting', response => {
				if (response.status === 'ok') {
					message.success(t('settings.vrsSettingsForm.activeSignalsMessages.restartConfirmation.success'));
				}
			});
		};
		return {
			isSubmitting,
			formState,
			validateInfos,
			onSubmit,
			onRestartConfirm,
			measurementsTimeIntervalOptions,
			restartRequired,
			showVrsFields,
			sessionStore,
		};
	},
}
</script>
